import { useEffect, useRef, useCallback } from 'react';

interface UseBarcodesScannerOptions {
  onScan: (barcode: string) => void;
  minLength?: number;
  maxInputDelay?: number;
  enabled?: boolean;
  scanEndKey?: string;
  prefixFilter?: string;
}

/**
 * Hook to detect hardware barcode scanner input.
 *
 * Hardware barcode scanners typically:
 * 1. Type characters very rapidly (faster than humanly possible)
 * 2. End with Enter key press
 * 3. Can be configured to add a prefix
 *
 * This hook detects rapid character sequences ending with Enter
 * and triggers the onScan callback with the scanned value.
 *
 * @param options Configuration options
 * @param options.onScan Callback function called with scanned barcode value
 * @param options.minLength Minimum barcode length to accept (default: 6)
 * @param options.maxInputDelay Max milliseconds between keystrokes (default: 50)
 * @param options.enabled Whether scanner detection is active (default: true)
 * @param options.scanEndKey Key that signals end of scan (default: 'Enter')
 * @param options.prefixFilter Only process barcodes starting with this prefix
 *
 * @example
 * ```tsx
 * useBarcodeScanner({
 *   onScan: (barcode) => {
 *     console.log('Scanned:', barcode);
 *     lookupMember(barcode);
 *   },
 *   prefixFilter: '5', // Only Bridge Club barcodes (start with 5)
 * });
 * ```
 */
export function useBarcodeScanner({
  onScan,
  minLength = 6,
  maxInputDelay = 50,
  enabled = true,
  scanEndKey = 'Enter',
  prefixFilter,
}: UseBarcodesScannerOptions) {
  const bufferRef = useRef<string>('');
  const lastKeyTimeRef = useRef<number>(0);
  const scanStartTimeRef = useRef<number>(0);

  const resetBuffer = useCallback(() => {
    bufferRef.current = '';
    lastKeyTimeRef.current = 0;
    scanStartTimeRef.current = 0;
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - lastKeyTimeRef.current;

      // If too much time has passed, reset the buffer
      // (user is typing manually, not scanning)
      if (timeSinceLastKey > maxInputDelay && bufferRef.current.length > 0) {
        resetBuffer();
      }

      // Check if this is the scan end key (usually Enter)
      if (event.key === scanEndKey) {
        const scannedValue = bufferRef.current.trim();

        // Validate the scanned value
        if (scannedValue.length >= minLength) {
          // Check time - scanners are fast (usually < 100ms for whole barcode)
          const totalScanTime = currentTime - scanStartTimeRef.current;
          const avgTimePerChar = totalScanTime / scannedValue.length;

          // If average time per char is very fast (< 30ms), it's likely a scanner
          if (avgTimePerChar < 30) {
            // Apply prefix filter if specified
            if (!prefixFilter || scannedValue.startsWith(prefixFilter)) {
              // Prevent default to avoid form submission
              event.preventDefault();
              onScan(scannedValue);
            }
          }
        }

        resetBuffer();
        return;
      }

      // Only capture printable characters
      if (event.key.length === 1) {
        // Start tracking time on first character
        if (bufferRef.current.length === 0) {
          scanStartTimeRef.current = currentTime;
        }

        bufferRef.current += event.key;
        lastKeyTimeRef.current = currentTime;
      }
    },
    [enabled, maxInputDelay, minLength, onScan, prefixFilter, resetBuffer, scanEndKey]
  );

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [enabled, handleKeyDown]);

  // Return helper function to manually trigger reset
  return { reset: resetBuffer };
}

export default useBarcodeScanner;
